import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import LoaderComponent from '@components/LoaderComponent';
import { toastError } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import BlockFileList from '@features/blockSuggestion/components/block/BlockFileList';
import ImageTab from '@features/blockSuggestion/components/ImageTab';
import SingleBlockImage from '@features/designTool/components/SingleBlockImage';
import UpdateFittingModal from '@features/designTool/components/UpdateFittingModal';
import {
	blockSuggestionsFilterFields,
	generateBlockSuggestionsParams
} from '@features/filter/static/filterUtil';
import BlockSuggestionsFilter from '@features/filter/v2/block-suggestions/BlockSuggestionsFilter';
import FilterComponent from '@features/filter/v2/FilterComponent';

import { setCurrentBlockAction } from '@store/actions/BlockAction';
import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { clearDesignViewModalInfo, setDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { clearGlobalFilter, toggleBlockSuggestionsFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { useBlockProductList } from '@store/reducers';
import { Types } from '@store/types';

import { fetchDependentFilterList } from '@services/commonService';
import { fetchProductBlocks } from '@services/measurementService';

import ApiClient from '@lib/apiClient';

import { isPageReachBottom } from '@utils/Util';

const ACTION_TYPES = ['SAVE', 'EDIT'];
const BlockSuggestion = ({ fromPage, selectedMeasurementType, fittingResponse, isEditable }) => {
	const blockStore = useSelector((store) => store.blockStore);
	const [selectedBlockResponse, setSelectedBlockResponse] = useState(null);
	const [isEditFittingInfo, setIsEditFittingInfo] = useState(false);
	const [selectedBlockId, setSelectedBlockId] = useState(null);
	const [designIdToView, setDesignIdToView] = useState(null);
	const [activeTab, setActiveTab] = useState('BLOCKS');
	const [blockListTab, setBlockListTab] = useState('ALL_STYLES');
	const [isViewFile, setIsViewFile] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const dispatch = useDispatch();
	const { id } = useParams();
	const { isShowBlockSuggestionFilter } = useSelector((store) => store.togleBlockSuggestionFilterStore);
	const filterRef = useRef();
	const filterfields = useSelector((store) => store.filterFieldStore);
	const blocks = useBlockProductList();
	const [page, setPage] = useState(0);
	const scrollRef = useRef({});
	const requestRef = useRef(null);

	useEffect(() => {
		const filterParams = generateBlockSuggestionsParams({
			fields: filterfields,
			id,
			addSearch: false,
			blockListTab
		});
		dispatch(fetchDependentFilterList({ type: 'BLOCK_SUGGESTIONS' }, filterParams));
	}, [filterfields, blockListTab]);

	useEffect(() => {
		if (selectedBlockId) {
			setActiveTab('BLOCKS');
			fetchProductFile(selectedBlockId);
		}
	}, [selectedBlockId]);

	useEffect(() => {
		if (!selectedBlockId) {
			setActiveTab('BLOCKS');
			setSelectedBlockId(id);
		}
	}, [blockStore]);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearch(search);
		}, 300);

		return () => {
			clearTimeout(handler);
		};
	}, [search]);

	useEffect(() => {
		getBlockProducts();
	}, [debouncedSearch, page, filterfields, blockListTab]);

	useEffect(() => {
		dispatch(clearGlobalFilter());
	}, [blockListTab]);

	const fetchProductFile = async (productId) => {
		dispatch(openLoader());
		try {
			const { data } = await ApiClient.GET('fetchProductMeasurementBlockInfo', productId);
			setSelectedBlockResponse({
				...data,
				hasMeasurementChart: data?.hasMeasurementChart,
				blockTitle: data?.blockTitle,
				blockRemarks: data?.blockRemarks,
				fittingTypeTag: data?.fittingTypeTag,
				inspirationBrand: data?.inspirationBrand,
				sizeStandardTag: data?.sizeStandardTag,
				lengthTag: data?.lengthTag,
				riseTag: data?.riseTag
			});
			setIsEditFittingInfo(false);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const handleOutsideClick = () => {
		dispatch(toggleBlockSuggestionsFilter(false));
	};
	useClickOutside(filterRef, handleOutsideClick);

	const onViewFile = (itemId) => {
		setIsViewFile(true);
		setSelectedBlockId(itemId);
	};

	useEffect(() => {
		if (Object.keys(filterfields).length > 1) {
			window.scrollTo(0, 1000);
			setPage(0);
			scrollRef.current.currentPage = 0;
		}
	}, [filterfields]);

	const setCurrentState = (data) => {
		scrollRef.current = data;
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = scrollRef.current;
			if (totalElements !== data?.length) {
				setPage(() => currentPage + 1);
			}
		}
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const getBlockProducts = async () => {
		if (requestRef.current) {
			requestRef.current.cancel();
		}

		const source = axios.CancelToken.source();
		requestRef.current = source;

		dispatch(openLoader());
		try {
			let params = generateBlockSuggestionsParams({
				fields: filterfields,
				page: page,
				id,
				search: debouncedSearch,
				addSearch: true,
				blockListTab
			});
			const response = await fetchProductBlocks(params, source.token);
			if (requestRef.current === source) {
				await dispatch({ type: Types.STORE_BLOCK_PRODUCT_LIST, payload: response });
				setCurrentState(response);
			}
		} finally {
			dispatch(closeLoader());
		}
	};

	const renderBlockListTabs = () => {
		return (
			<div className="inner-tabs">
				<ul>
					<li
						className={blockListTab === 'BLOCKS_LIST' ? 'active' : ''}
						onClick={() => setBlockListTab('BLOCKS_LIST')}
					>
						Blocks
					</li>
					<li
						className={blockListTab === 'ALL_STYLES' ? 'active' : ''}
						onClick={() => setBlockListTab('ALL_STYLES')}
					>
						All styles
					</li>
				</ul>
			</div>
		);
	};

	const renderBlockFiles = () => {
		return (
			<>
				<div className="suggested-items">
					<div className={`${fromPage === 'MEASUREMENT' ? 'd-flex justify-content-between' : ''}`}>
						<div>
							<div>
								{fromPage === 'MEASUREMENT' && (
									<>
										{renderBlockListTabs()}
										<div>
											<BlockSuggestionsFilter />
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
				<section className="filter-data-container filter-v3-data-container mb-8" ref={filterRef}>
					<FilterComponent
						showFilter={isShowBlockSuggestionFilter}
						filterPageName="BLOCK_SUGGESTIONS"
						isDependentFilter={true}
						fields={blockSuggestionsFilterFields}
					/>
					<div className="data-table">
						<div className="form-group size32 search-filter block-search mt-12">
							<div className="with-icon right-icon">
								<input
									type="text"
									autoComplete="off"
									placeholder="Search with Block title or design number & add to this techpack"
									name="search"
									onChange={(e) => setSearch(e.target.value)}
								/>
								<span className="right-icon">
									<img
										src={`${process.env.PUBLIC_URL}/icons/search-dark.svg`}
										alt={`search_icon`}
									/>
								</span>
							</div>
						</div>
						<div className="similar-styles-section">
							{blocks?.blockProductList?.length > 0 ? (
								<div className="design-list-container">
									<div className="design-list-row">
										{blocks?.blockProductList?.map((item, index) => (
											<div key={`${item?.id}_${index}`}>
												<SingleBlockImage
													item={item}
													setDesignIdToView={setDesignIdToView}
													onViewFiles={() => onViewFile(item?.id)}
													blockListTab={blockListTab}
												/>
											</div>
										))}
									</div>
								</div>
							) : (
								<div className="d-flex pt-5 mt-5 gray_dark_03 justify-content-center align-content-center">
									<span className="regular-14  gray-1">No suggestion found</span>
								</div>
							)}
						</div>
					</div>
				</section>
			</>
		);
	};

	const renderBlockList = () =>
		fromPage === 'MEASUREMENT' ? renderBlockFiles() : !isViewFile ? renderBlockFiles() : '';

	const onTabChange = (tab) => {
		if (tab === 'IMAGES') {
			if (fromPage === 'MEASUREMENT' && ACTION_TYPES.includes(blockStore?.currentBlockAction)) {
				setSelectedBlockId(id);
			}
			setSelectedBlockId(selectedBlockResponse?.id);
			setActiveTab(tab);
		} else {
			setSelectedBlockId(selectedBlockResponse?.id);
			setActiveTab(tab);
		}
	};

	const renderTabs = () => (
		<div className="tab image-file-tab">
			<ul>
				<li
					className={`regular-12 weight-500 ${activeTab === 'IMAGES' ? 'active' : ''}`}
					onClick={() => onTabChange('IMAGES')}
				>
					IMAGES
				</li>
				<li
					className={`regular-12 weight-500 ${activeTab === 'BLOCKS' ? 'active' : ''}`}
					onClick={() => onTabChange('BLOCKS')}
				>
					BLOCK SUGGESTION
				</li>
			</ul>
			{fromPage === 'MEASUREMENT' && (
				<div className="close-btn" onClick={onClosePanel}>
					<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
				</div>
			)}
		</div>
	);

	const onClosePanel = () => {
		setSelectedBlockId(null);
		setIsViewFile(false);
		dispatch(setCurrentBlockAction(null));
	};

	const onAddSuccess = () => {
		if (fromPage === 'MEASUREMENT' && ACTION_TYPES.includes(blockStore?.currentBlockAction)) {
			setIsLoading(true);
			dispatch(fetchProductInfo(id)).finally(() => {
				setIsLoading(false);
			});
		}
		fetchProductFile(id);
	};

	useEffect(() => {
		if (designIdToView !== null) {
			openDesignViewModal();
		}
	}, [designIdToView]);

	const onCloseViewModal = () => {
		setIsLoading(true);
		dispatch(fetchProductInfo(id)).finally(() => {
			setIsLoading(false);
		});
		setDesignIdToView(null);
		dispatch(clearDesignViewModalInfo({}));
	};

	const openDesignViewModal = () => {
		dispatch(
			setDesignViewModalInfo({
				id: designIdToView,
				onCloseViewModal: onCloseViewModal,
				source: 'PRODUCT_LIST'
			})
		);
	};

	return (
		<LoaderComponent loading={isLoading}>
			{(isViewFile || ACTION_TYPES.includes(blockStore?.currentBlockAction)) && (
				<div className="block-suggestions-sidebar scroll-y-label">
					{fromPage === 'MEASUREMENT' && renderTabs()}
					{activeTab === 'BLOCKS' ? (
						<BlockFileList
							fromPage={fromPage}
							onBack={() => setIsViewFile(false)}
							selectedBlockResponse={selectedBlockResponse}
							onAddSuccess={onAddSuccess}
							onSelectDesign={() => setDesignIdToView(selectedBlockResponse?.id)}
							onClosePanel={onClosePanel}
							isEditable={isEditable}
							selectedMeasurementType={selectedMeasurementType}
						/>
					) : (
						<ImageTab id={selectedBlockId} />
					)}
				</div>
			)}
			{renderBlockList()}
			{isEditFittingInfo && (
				<UpdateFittingModal
					isShowClose={true}
					productId={id}
					blockResponse={fittingResponse}
					onAddSuccess={() => fetchProductFile(id)}
					onCloseModal={() => setIsEditFittingInfo(false)}
					selectedMeasurementType={selectedMeasurementType}
				/>
			)}
		</LoaderComponent>
	);
};

export default BlockSuggestion;
